import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardList, faUsers } from "@fortawesome/free-solid-svg-icons";
import SEO from "../../components/common/SEO";
import { generateBreadcrumbSchema } from "../../utils/seo/structuredData";
import { getCurrentWeekRange } from "../../utils/eventTimingHelper";
import Loading from "../../components/common/Loading";
import { useUserLeagues } from "../../hooks/league/useFetchUserLeagues";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import { useFetchScheduledEvents } from "../../hooks/events/useFetchScheduledEvents";
import { useFetchLeagueMemberSubmissions } from "../../hooks/lineup/useFetchLeagueMemberSubmissions";
import { useFetchFollowingSubmissions } from "../../hooks/lineup/useFetchFollowingSubmissions";
import { useFetchUserEventLineup } from "../../hooks/lineup/useFetchUserEventLineup";
import { useLeagueMembers } from "../../hooks/league/useFetchLeagueMembers";
import { isEventEligibleForLeague } from "../../utils/league/leagueHelpers";
import { useFetchAllUserLineups } from "../../hooks/lineup/useFetchAllUserLineups";
import FriendsGlobalView from "../../components/scorecard/FriendsGlobalView";
import LeagueScorecardView from "../../components/scorecard/LeagueScorecardView";

const ScorecardPage = () => {
  const navigate = useNavigate();
  const currentLeagueId = useSelector((state) => state.league.leagueId);
  const globalLeagueId = process.env.REACT_APP_GLOBAL_LEAGUE_ID;

  const [activeTab, setActiveTab] = useState("league");
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [submissionsPerPage] = useState(20);

  const { data: dbUser, isLoading: isUserLoading } = useFetchUser();
  const { data: leaguesData, isLoading: isLeaguesLoading } = useUserLeagues(
    dbUser?._id,
  );
  const { data: eventsData, isLoading: isEventsLoading } =
    useFetchScheduledEvents();

  // Fetch ALL user's global competition submissions (past and future) to filter events
  const { data: userGlobalLineups } = useFetchAllUserLineups(
    globalLeagueId,
    dbUser?._id,
  );

  // Filter to only private leagues (exclude global)
  const privateLeagues = React.useMemo(
    () =>
      leaguesData?.memberOf?.filter(
        (league) => league._id !== globalLeagueId && league.status === "active",
      ) || [],
    [leaguesData, globalLeagueId],
  );

  // Determine which league ID to use based on active tab
  const effectiveLeagueId = React.useMemo(() => {
    if (activeTab === "friends") {
      return globalLeagueId;
    }
    return selectedLeagueId;
  }, [activeTab, selectedLeagueId, globalLeagueId]);

  // Set default private league
  useEffect(() => {
    if (!selectedLeagueId && privateLeagues.length > 0) {
      const defaultLeague =
        privateLeagues.find((l) => l._id === currentLeagueId) ||
        privateLeagues[0];
      setSelectedLeagueId(defaultLeague._id);
    }
  }, [privateLeagues, currentLeagueId, selectedLeagueId]);

  // Reset page when league or event changes
  useEffect(() => {
    setCurrentPage(1);
  }, [effectiveLeagueId, selectedEventId]);

  // Get events for the effective league
  const leagueEvents = React.useMemo(() => {
    if (!effectiveLeagueId || !eventsData) return [];

    const isGlobal = effectiveLeagueId === globalLeagueId;

    if (isGlobal) {
      const now = new Date();
      const sorted = [...eventsData].sort(
        (a, b) => new Date(a.DateTime) - new Date(b.DateTime),
      );
      let currentIndex = sorted.findIndex((e) => new Date(e.DateTime) >= now);
      if (currentIndex === -1) {
        currentIndex = sorted.length - 1;
      }
      const start = Math.max(0, currentIndex - 2);
      const end = Math.min(sorted.length, currentIndex + 3);
      return sorted.slice(start, end);
    }

    const league = privateLeagues.find((l) => l._id === effectiveLeagueId);
    if (!league) return [];

    return eventsData
      .filter((event) => {
        return isEventEligibleForLeague(event.EventId, league, event.DateTime);
      })
      .sort((a, b) => new Date(a.DateTime) - new Date(b.DateTime));
  }, [effectiveLeagueId, eventsData, privateLeagues, globalLeagueId]);

  // Set default event
  useEffect(() => {
    if (leagueEvents.length > 0) {
      const now = new Date();
      const pastOrCurrentEvents = leagueEvents.filter(
        (e) => new Date(e.DateTime) <= now,
      );
      const upcomingEvents = leagueEvents.filter(
        (e) => new Date(e.DateTime) > now,
      );
      const upcomingThisWeek = upcomingEvents.find((e) => {
        const eventDate = new Date(e.DateTime);
        const { startOfWeek, endOfWeek } = getCurrentWeekRange();
        return eventDate >= startOfWeek && eventDate <= endOfWeek;
      });

      let defaultEvent;
      if (upcomingThisWeek) {
        defaultEvent = upcomingThisWeek;
      } else if (pastOrCurrentEvents.length > 0) {
        defaultEvent = pastOrCurrentEvents[pastOrCurrentEvents.length - 1];
      } else {
        defaultEvent = upcomingEvents[0];
      }

      setSelectedEventId(defaultEvent?.EventId || leagueEvents[0].EventId);
    } else {
      setSelectedEventId(null);
    }
  }, [leagueEvents]);

  const isGlobalLeague = effectiveLeagueId === globalLeagueId;

  // Fetch submissions
  const followingSubmissionsQuery = useFetchFollowingSubmissions(
    effectiveLeagueId,
    selectedEventId,
    currentPage,
    submissionsPerPage,
  );

  const allMemberSubmissionsQuery = useFetchLeagueMemberSubmissions(
    effectiveLeagueId,
    selectedEventId,
  );

  const userOwnSubmissionQuery = useFetchUserEventLineup(
    effectiveLeagueId,
    selectedEventId,
    dbUser?._id,
  );

  const submissionsQueryResult = React.useMemo(() => {
    if (isGlobalLeague) {
      const followingHasError =
        followingSubmissionsQuery.error?.response?.status === 404;

      if (followingHasError && userOwnSubmissionQuery.data) {
        return {
          data: {
            submissions: userOwnSubmissionQuery.data
              ? [userOwnSubmissionQuery.data]
              : [],
            pagination: {
              currentPage: 1,
              totalPages: 1,
              total: userOwnSubmissionQuery.data ? 1 : 0,
              limit: 20,
              hasNext: false,
              hasPrevious: false,
            },
          },
          isLoading: userOwnSubmissionQuery.isLoading,
          error: userOwnSubmissionQuery.error,
        };
      }

      return {
        data: followingSubmissionsQuery.data,
        isLoading: followingSubmissionsQuery.isLoading,
        error: followingSubmissionsQuery.error,
      };
    }

    return {
      data: allMemberSubmissionsQuery.data,
      isLoading: allMemberSubmissionsQuery.isLoading,
      error: allMemberSubmissionsQuery.error,
    };
  }, [
    isGlobalLeague,
    followingSubmissionsQuery.data,
    followingSubmissionsQuery.isLoading,
    followingSubmissionsQuery.error,
    allMemberSubmissionsQuery.data,
    allMemberSubmissionsQuery.isLoading,
    allMemberSubmissionsQuery.error,
    userOwnSubmissionQuery.data,
    userOwnSubmissionQuery.isLoading,
    userOwnSubmissionQuery.error,
  ]);

  const submissionsData = submissionsQueryResult.data;

  // Fetch league members. Private leagues need the full roster to compute
  // non-submitters; the global league only needs the total count (its rows are
  // never rendered here), so avoid pulling a thousand member docs for it.
  const { data: leagueMembersData } = useLeagueMembers(
    effectiveLeagueId,
    1,
    isGlobalLeague ? 1 : 1000,
    "",
  );

  const selectedEvent = leagueEvents.find(
    (e) =>
      e.EventId === selectedEventId || e.EventId === parseInt(selectedEventId),
  );
  const isEventStarted = selectedEvent
    ? new Date(selectedEvent.DateTime) <= new Date()
    : false;

  // Process submissions data
  const submissions = React.useMemo(() => {
    const rawSubmissions = isGlobalLeague
      ? submissionsData?.submissions || []
      : submissionsData || [];

    if (isGlobalLeague && rawSubmissions.length > 0) {
      return [...rawSubmissions].sort((a, b) => {
        if (a.isOwnSubmission && !b.isOwnSubmission) return -1;
        if (!a.isOwnSubmission && b.isOwnSubmission) return 1;
        return 0;
      });
    }

    return rawSubmissions;
  }, [isGlobalLeague, submissionsData]);

  const submittedUserIds = React.useMemo(() => {
    return new Set(
      submissions.map((sub) => {
        if (typeof sub.userId === "object" && sub.userId !== null) {
          return sub.userId._id;
        }
        return sub.userId;
      }) || [],
    );
  }, [submissions]);

  const selectedLeague = privateLeagues.find((l) => l._id === selectedLeagueId);
  const totalMembers = isGlobalLeague
    ? leagueMembersData?.total || 0
    : selectedLeague?.totalMembers || selectedLeague?.members?.length || 0;

  const membersWithoutSubmission = React.useMemo(() => {
    if (isGlobalLeague) return [];
    const allMembers = leagueMembersData?.data || [];
    return allMembers.filter((member) => !submittedUserIds.has(member.userId));
  }, [leagueMembersData?.data, submittedUserIds, isGlobalLeague]);

  const submissionCount = submissions.length || 0;

  const breadcrumbs = [
    { name: "Home", path: "/home" },
    { name: "Leagues", path: "/leagues" },
    { name: "Scorecard", path: "/scorecard" },
  ];

  // Redirect if no user data
  if (!isUserLoading && !dbUser) {
    navigate("/");
    return null;
  }

  // Show fullscreen loading for initial data fetch
  if (isUserLoading || isLeaguesLoading || isEventsLoading) {
    return <Loading />;
  }

  const hasGlobalEntries = userGlobalLineups && userGlobalLineups.length > 0;
  const hasPrivateLeagues = privateLeagues.length > 0;

  // If user has neither leagues nor global entries
  if (!hasPrivateLeagues && !hasGlobalEntries) {
    return (
      <div className="space-y-4 pb-6">
        <ScorecardHeaderBanner />
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon
              icon={faUsers}
              className="text-medium-grey w-8 h-8"
            />
          </div>
          <h3 className="text-xl font-bold text-dark-grey mb-2">
            No Leagues Yet
          </h3>
          <p className="text-medium-grey mb-6">
            Create or join a league to track member submissions
          </p>
          <button
            onClick={() => navigate("/create-league")}
            className="bg-deep-red hover:bg-bright-red text-white px-6 py-3 rounded-xl font-bold transition-colors"
          >
            Create Your First League
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    ...(hasPrivateLeagues ? [{ id: "league", label: "My League" }] : []),
    ...(hasGlobalEntries ? [{ id: "friends", label: "Following" }] : []),
  ];

  // If only one tab option exists, auto-select it
  if (tabs.length === 1 && activeTab !== tabs[0].id) {
    setActiveTab(tabs[0].id);
  }

  return (
    <>
      <SEO
        title="Scorecard - Track League Submissions | DayShift MMA"
        description="View which league members have submitted their lineups for upcoming UFC events. Track submission status and view lineups after events start."
        keywords="UFC fantasy scorecard, lineup submissions, league tracking, fantasy MMA submissions"
        url="/scorecard"
        structuredData={generateBreadcrumbSchema(breadcrumbs)}
      />

      <div className="space-y-0 pb-6">
        {/* Scorecard Header */}
        <ScorecardHeaderBanner />

        {/* Pill Tab Navigation */}
        {tabs.length > 1 && (
          <div className="sticky top-[60px] z-30 bg-white/95 backdrop-blur-sm border-b border-gray-200 -mx-5 px-5 md:-mx-0 md:px-0">
            <div className="py-2">
              <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      if (activeTab !== tab.id) {
                        setActiveTab(tab.id);
                        setSelectedEventId(null);
                      }
                    }}
                    className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${
                      activeTab === tab.id
                        ? "bg-white text-dark-grey shadow-sm"
                        : "text-medium-grey hover:text-dark-grey"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="pt-4 space-y-4">
          {/* Friends Tab - Uses dedicated FriendsGlobalView */}
          {activeTab === "friends" && (
            <FriendsGlobalView
              submissions={submissions}
              eventName={selectedEvent?.Name}
              eventDate={
                selectedEvent
                  ? new Date(selectedEvent.DateTime).toLocaleDateString(
                      "en-US",
                      {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      },
                    )
                  : null
              }
              isEventStarted={isEventStarted}
              totalEntries={leagueMembersData?.total || 0}
              events={leagueEvents}
              selectedEventId={selectedEventId}
              onEventChange={(id) => setSelectedEventId(id)}
            />
          )}

          {/* My League Tab */}
          {activeTab === "league" && (
            <LeagueScorecardView
              submissions={submissions}
              membersWithoutSubmission={membersWithoutSubmission}
              leagueName={selectedLeague?.leagueName}
              eventName={selectedEvent?.Name}
              eventDate={
                selectedEvent
                  ? new Date(selectedEvent.DateTime).toLocaleDateString(
                      "en-US",
                      { month: "long", day: "numeric", year: "numeric" },
                    )
                  : null
              }
              isEventStarted={isEventStarted}
              totalMembers={totalMembers}
              submittedCount={submissionCount}
              leagues={privateLeagues}
              selectedLeagueId={selectedLeagueId}
              onLeagueChange={(id) => {
                setSelectedLeagueId(id);
                setSelectedEventId(null);
              }}
              events={leagueEvents}
              selectedEventId={selectedEventId}
              onEventChange={(id) => setSelectedEventId(id)}
            />
          )}
        </div>
      </div>
    </>
  );
};

/**
 * Scorecard header banner matching the app's design pattern
 */
const ScorecardHeaderBanner = () => (
  <div className="bg-gradient-to-br from-dark-grey via-gray-700 to-gray-800 rounded-xl p-4 text-white shadow-lg mb-4">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-deep-red flex items-center justify-center shadow-lg">
        <FontAwesomeIcon
          icon={faClipboardList}
          className="w-5 h-5 text-white"
        />
      </div>
      <div>
        <h1 className="text-lg font-bold text-white">Scorecard</h1>
        <p className="text-white/70 text-xs">Track league member submissions</p>
      </div>
    </div>
  </div>
);

export default ScorecardPage;
